import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Input,
    FormControl,
    FormLabel,
    useToast,
    useDisclosure,
} from '@chakra-ui/react';
import { MdAddBox, MdDelete } from 'react-icons/md';
import fetchWrapper from '../lib/fetch-wrapper';
import { useMsal } from '@azure/msal-react';
import { useProfile } from '../lib/profileContext';

const ConfigPage = () => {
    const { instance, accounts } = useMsal();
    const { profile } = useProfile();
    const [records, setRecords] = useState<any[]>([]);
    const [formData, setFormData] = useState({ Name: '', Description: '' });
    const [isLoading, setIsLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    useEffect(() => {
        void fetchProperties();
    }, []);

    const fetchProperties = async () => {
        try {
            const fetchInstance = new fetchWrapper();
            const response: any = await fetchInstance.getProperties();
            const sorted = response.data.records.sort((a: any, b: any) =>
                a.data.Name.localeCompare(b.data.Name)
            );
            setRecords(sorted);
        } catch (error) {
            toast({ title: 'Error fetching properties', status: 'error', duration: 3000 });
        }
    };

    const handleAdd = () => {
        setFormData({ Name: '', Description: '' });
        onOpen();
    };

    const handleSubmit = async () => {
        if (!formData.Name.trim()) {
            toast({ title: 'Validation Error', description: 'Name is required.', status: 'error', duration: 3000 });
            return;
        }
        try {
            setIsLoading(true);
            const fetchInstance = new fetchWrapper();
            await fetchInstance.addProperty(formData);
            toast({ title: 'Property added', status: 'success', duration: 3000 });
            onClose();
            await fetchProperties();
        } catch (error) {
            toast({ title: 'Error adding property', status: 'error', duration: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (rowKey: string) => {
        if (!window.confirm('Are you sure you want to delete this property?')) return;
        try {
            const fetchInstance = new fetchWrapper();
            await fetchInstance.deleteProperty(rowKey);
            toast({ title: 'Property deleted', status: 'success', duration: 3000 });
            await fetchProperties();
        } catch (error) {
            toast({ title: 'Error deleting property', status: 'error', duration: 3000 });
        }
    };

    return (
        <Box mx="auto" mt="6" bg="#ffffff" height="100%" m="0" px="30px" pt="30px" overflowY="auto">
            <Button
                variant="brand"
                size="lg"
                height="4rem"
                leftIcon={<MdAddBox size="1.5em" />}
                onClick={handleAdd}
            >
                Add Property
            </Button>

            <Table variant="simple" mt="6">
                <Thead>
                    <Tr>
                        <Th>Name</Th>
                        <Th>Description</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {records.map((record) => (
                        <Tr key={record.RowKey}>
                            <Td>{record.data.Name}</Td>
                            <Td>{record.data.Description || '-'}</Td>
                            <Td>
                                <Button
                                    bg="transparent"
                                    leftIcon={<MdDelete color="black" />}
                                    onClick={() => handleDelete(record.RowKey)}
                                />
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>

            <Modal isOpen={isOpen} size="lg" onClose={onClose} closeOnOverlayClick={!isLoading}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Add Property</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl isRequired>
                            <FormLabel>Name</FormLabel>
                            <Input
                                value={formData.Name}
                                onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                                placeholder="Enter property name"
                            />
                        </FormControl>
                        <FormControl mt={4}>
                            <FormLabel>Description</FormLabel>
                            <Input
                                value={formData.Description}
                                onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                                placeholder="Enter description (optional)"
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="cancel" onClick={onClose} mr={3} isDisabled={isLoading}>
                            Cancel
                        </Button>
                        <Button variant="brand" onClick={handleSubmit} isLoading={isLoading}>
                            Add
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default ConfigPage;
